import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listingsApi, categoriesApi, getPhotoUrl, FALLBACK_LISTING_IMAGE } from '../utils/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MapPin, Funnel } from '@phosphor-icons/react';

// Category-specific images for demo listings (matching HomePage)
const CATEGORY_LISTING_IMAGES = {
  'reading-room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'reading_room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'workspace': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
  'home-gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home_gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home-theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'home_theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'game-room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'game_room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'backyard': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'pool': 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&q=80',
  'dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'private_dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'hobby-room': 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&q=80',
  'wellness': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
  'creator-studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'creative_studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'outdoor-sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'outdoor_sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
};

// Get appropriate image for a listing
const getListingImage = (listing) => {
  // If listing has real photos, use them
  if (listing.photos && listing.photos.length > 0 && listing.photos[0]) {
    const photoUrl = getPhotoUrl(listing.photos[0]);
    if (photoUrl && !photoUrl.includes('undefined')) {
      return photoUrl;
    }
  }
  // Otherwise, use category-specific curated image
  return CATEGORY_LISTING_IMAGES[listing.category] || CATEGORY_LISTING_IMAGES['default'];
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    category: searchParams.get('category') || '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadListings();
  }, [filters]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadListings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.location) params.location = filters.location;
      
      const response = await listingsApi.getAll(params);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to load listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? cat.name : slug;
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Filters */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-6">Find Your Perfect Space</h1>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex items-center gap-2 bg-[#121212] border border-white/20 px-4">
              <MapPin size={20} className="text-zinc-500" weight="duotone" />
              <Input
                data-testid="filter-location-input"
                type="text"
                placeholder="City or Zip Code"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="bg-transparent border-none text-white"
              />
            </div>
            
            <div className="flex-1 flex items-center gap-2 bg-[#121212] border border-white/20 px-4">
              <Funnel size={20} className="text-zinc-500" weight="duotone" />
              <select
                data-testid="filter-category-select"
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full bg-transparent border-none text-white py-3 focus:outline-none"
              >
                <option value="" className="bg-[#121212]">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.slug} className="bg-[#121212]">
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <Button
              data-testid="apply-filters-button"
              onClick={loadListings}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold px-8"
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center text-zinc-400 py-12">Loading...</div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-400 text-lg">No spaces found. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <p className="text-zinc-400 mb-6">{listings.length} spaces found</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {listings.map((listing) => (
                <div
                  key={listing.id}
                  data-testid={`search-listing-${listing.id}`}
                  onClick={() => navigate(`/listing/${listing.id}`)}
                  className="group bg-[#121212] border border-white/10 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
                >
                  <div className="h-64 overflow-hidden">
                    <img
                      src={getListingImage(listing)}
                      alt={listing.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = CATEGORY_LISTING_IMAGES[listing.category] || CATEGORY_LISTING_IMAGES['default'];
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-serif text-white flex-1">
                        {listing.title}
                      </h3>
                      {listing.average_rating > 0 && (
                        <div className="flex items-center gap-1 text-[#D4AF37]">
                          <span>★</span>
                          <span className="text-sm">{listing.average_rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 mb-4">
                      {listing.location_city}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-white font-semibold">
                        ${listing.price_per_hour}<span className="text-zinc-500 text-sm font-normal">/hour</span>
                      </p>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">
                        {getCategoryName(listing.category)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}